import { Component, Inject, OnDestroy, OnInit, TrackByFunction } from '@angular/core';

import {
  BehaviorSubject,
  catchError,
  combineLatest,
  filter,
  merge,
  of,
  Subscription,
  switchMap,
  take,
  tap,
  finalize,
} from 'rxjs';
import { Angulartics2 } from 'angulartics2';

import { makeMap } from '@shared/core';
import { Location } from '@shared/location/models';
import { PricePlan } from '@shared/price-plan/models';
import { PaymentPlanCodes } from '@shared/price-plan/enums';
import { SubscriptionTypes } from '@shared/restaurant/enums';
import { IWaitingPartiesResponse } from '@shared/party/models';
import { AuthUser } from '@shared/signup/models';
import { Account } from '@shared/account/models';
import { SubscriptionState } from '@shared/recurly/enums';
import {
  ConfirmationModalComponent,
  ConfirmationModalRef, ConfirmationModalResult,
  ConfirmationModalService,
  DropdownFilterItem,
  InfoModalComponent,
} from '@tablesready/common-ui';
import { AddEditLocationComponent } from '../../../modals';
import {
  LocationApiService,
  LocationUsersService,
  PartiesService,
} from '../../../services';
import { ILocationFilters } from './models';
import { LocationsListService } from './locations-list.service';
import { availabilityFilters } from './constants';
import { CopySettingsModalComponent } from './modals';
import { FrontendModalService } from '@tablesready/frontend-modal';
import { AppIcon, AppIconColor } from '@tablesready/frontend-icons';
import {
  AuthRestaurantService,
  LocationsService,
  PricePlansApiService,
} from '@tablesready/frontend-store';
import { LocationSwitcherService } from '@tablesready/frontend-location-switcher';
import { AlertsService } from '@tablesready/frontend-alerts-bar';
import { APP_VARIABLES_TOKEN, AppVariables } from '@tablesready/frontend-variables';

@Component({
  selector: 'tablesready-locations',
  templateUrl: './locations.component.html',
  styleUrls: ['./locations.component.scss'],
})
export class LocationsComponent implements OnInit, OnDestroy {
  user: AuthUser;
  icon = AppIcon;
  iconColor = AppIconColor;
  locations: Location[] = [];
  locationsWithFreePlanCounter: number;
  currentLocationId: string;
  page = 1;
  pageSize = 5;
  maxSize = 5;
  pricePlansMap: Record<string, PricePlan> = {};
  locationUsersMap: Record<string, number> = {};
  waitlistedParties: IWaitingPartiesResponse[] = [];
  enabledLocationsCounter: number;
  subscriptionTypes = SubscriptionTypes;
  subscriptionType?: SubscriptionTypes;
  dropdownPricePlans: DropdownFilterItem<string | null>[] = [];
  availabilityFilters = availabilityFilters;
  allAccountLocationsCounter?: number;
  accountId?: string;
  bannedLocations = '';
  locationsTogglesDisabled = false;
  selectedPlan?: string;
  availabilityFilter?: string;
  readonly trackById: TrackByFunction<Location> = (idx, { _id }) => _id;

  private filtersSource$ = new BehaviorSubject<ILocationFilters>({
    info: '',
    planCode: null,
    availability: null,
  });

  private readonly subscriptions = new Subscription();
  private confirmationRef?: ConfirmationModalRef<any>;
  private isOffline = false;
  private account: Account;

  constructor(
    private readonly modalController: FrontendModalService,
    private readonly locationService: LocationsService,
    private readonly locationApiService: LocationApiService,
    private readonly alertsService: AlertsService,
    private readonly locationSwitcherService: LocationSwitcherService,
    private readonly partyService: PartiesService,
    private readonly confirmationModalService: ConfirmationModalService,
    private readonly authRestaurantService: AuthRestaurantService,
    private readonly pricePlansApiService: PricePlansApiService,
    private readonly locationUsersService: LocationUsersService,
    private readonly locationsListService: LocationsListService,
    private readonly angulartics2: Angulartics2,
    @Inject(APP_VARIABLES_TOKEN) public readonly appVariables: AppVariables,
  ) {}

  ngOnInit(): void {
    this.subscriptions.add(
      combineLatest([
        this.authRestaurantService.select().pipe(
          filter(user => !!user.location),
          tap(user => {
            this.user = user;
            this.account = user.account;
            this.accountId = user.account.id;
            this.subscriptionType = user.account.subscriptionType;
            this.currentLocationId = user.location._id;
            this.isOffline = user.account.subscriptionType === SubscriptionTypes.offline;
          }),
        ),
        this.locationsListService.selectLocations(this.filtersSource$).pipe(
          filter(([locations]) => !locations.includes(undefined)),
          tap(([locations, locationUsersMap]) => {
            this.locations = locations.filter(loc => loc.accountId === this.accountId);
            this.locationUsersMap = locationUsersMap;
            this.bannedLocations = locations.filter(loc => loc.banned)
              .map(loc => (`"${loc.name}"`))
              .join(',');
          }),
        ),
        this.authRestaurantService.selectWithAccountAndLocation().pipe(
          switchMap(() => combineLatest([
            this.pricePlansApiService.getByAccount(),
            this.locationService.selectAll(),
          ])),
          tap(([pricePlans, locations]) => {
            this.pricePlansMap = makeMap(pricePlans, 'plan_code');
            this.allAccountLocationsCounter = locations.filter(location => location.accountId === this.accountId).length;
            this.enabledLocationsCounter = locations.filter(location => location.accountId === this.accountId && location.enabled).length;
            this.setPricePlans(locations, this.pricePlansMap);
          }),
        ),
      ]).pipe(
        filter(([user]) => user.account !== undefined),
        switchMap(([user]) => {
          if (user.location && this.locations.length) {
            return this.partyService.getWaitlistedParties(this.locations.map(({_id}) => _id));
          }

          return of([]);
        }),
        tap(waitlistedParties => {
          this.waitlistedParties = waitlistedParties;
          this.locationsWithFreePlanCounter = 0;
          this.locationsTogglesDisabled = false;
        }),
        filter(() => !this.isOffline),
        switchMap(() => this.locationService.countFreeLocations()),
        tap(countOfFreeLocations => {
          this.locationsWithFreePlanCounter = countOfFreeLocations;
        }),
      ).subscribe(),
    );

    this.subscriptions.add(
      merge(
        this.locationService.listenToChanges(),
        this.locationUsersService.listenToChanges(),
      ).subscribe(),
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();

    if (this.confirmationRef) {
      this.confirmationRef.dismiss();
    }
  }

  updateLocationAvailability(location: Location): void {
    const isBanned = location.banned;
    const isAtLimit = location.enabled && location?.recurly?.planCode === PaymentPlanCodes.Free &&
      this.locationsWithFreePlanCounter >= this.appVariables.FREE_PLAN_LOCATION_LIMIT;

    let message = 'This location has been disabled by the SMS spam checker. TablesReady support will review the circumstances within 24 hours';
    if (isAtLimit) {
      message = `You have reached the limit of ${this.appVariables.FREE_PLAN_LOCATION_LIMIT} Free locations. In order to create a new location or activate an inactive Free location, you must first disable, delete or activate a paid subscription for one of your existing locations.`;
    }

    if (isBanned || isAtLimit) {
      this.modalController.create<InfoModalComponent, unknown>(InfoModalComponent, {
        message,
        displayButton: true,
        buttonText: 'OK',
      })
        .pipe(
          take(1),
          take(1),
          finalize(() => {
            location.enabled = false;
          }),
        )
        .subscribe();

      return;
    }

    if (location.enabled) {
      this.locationsTogglesDisabled = true;
      this.locationApiService.updateAvailability(location._id, location.enabled)
        .pipe(
          tap(() => {
            this.alertsService.addSuccess('Location enabled successfully!', 'Updated: ');
          }),
          catchError(() => {
            this.alertsService.addError('error disabling location.');
            location.enabled = !location.enabled;

            return of(null);
          }),
        )
        .subscribe();
    } else {
      if (location.recurly.subscription_state === SubscriptionState.paused) {
        this.alertsService.addWarning('You cannot disable this location due to paused subscription. Please, reactivate it and try again.');
        location.enabled = true;

        return;
      }

      const isRecurlyAccount = this.subscriptionType === SubscriptionTypes.recurly;
      const isLocationPaid = location?.recurly?.planCode !== PaymentPlanCodes.Free;
      let content = 'Are you sure you want to disable this location?';

      if (isLocationPaid && isRecurlyAccount) {
        content = 'You are disabling a location with an active subscription. This subscription will be canceled. Proceed?';
      }

      this.modalController.create<ConfirmationModalComponent, ConfirmationModalResult<boolean>>(ConfirmationModalComponent, {
        options: {
          content,
          closeBtnText: 'Cancel',
        },
      })
        .pipe(
          take(1),
          filter((res) => {
            if (!res || !res.response) {
              location.enabled = true;
              return false;
            }

            location.enabled = false;
            return true;
          }),
          switchMap(() => this.partyService.getUpcoming(location._id)),
          switchMap((count) => {
            if (count === 0) {
              return of({ isConfirmed: true });
            }

            return this.confirmationModalService.confirm({
              content: 'This location has upcoming parties. Are you sure you want to disable it?',
              closeBtnText: 'Cancel',
            });
          }),
          take(1),
          filter((result: ConfirmationModalRef<boolean>) => {
            if (!result.isConfirmed) {
              location.enabled = true;
            }

            return result.isConfirmed;
          }),
          switchMap(() => {
            this.locationsTogglesDisabled = true;

            return this.locationApiService.updateAvailability(location._id, location.enabled);
          }),
          tap(() => {
            this.alertsService.addSuccess('Location disabled successfully!', 'Updated: ');
          }),
          catchError((err) => {
            this.alertsService.addError('error disabling location.');
            location.enabled = !location.enabled;

            return of(err);
          }),
        )
        .subscribe();
    }
  }

  setFilters(filter: Partial<ILocationFilters>): void {
    this.filtersSource$.next({...this.filtersSource$.getValue(), ...filter});
  }

  navigateToLocationPage(locationId: string, url: string): void {
    this.locationSwitcherService.navigateToLocationPage(locationId, url);
  }

  addLocation(): void {
    if (this.locationsWithFreePlanCounter >= this.appVariables.FREE_PLAN_LOCATION_LIMIT) {
      this.modalController.create<InfoModalComponent, unknown>(InfoModalComponent, {
        message: `You have reached the limit of ${this.appVariables.FREE_PLAN_LOCATION_LIMIT} Free locations. In order to create a new location or activate an inactive Free location, you must first disable, delete or activate a paid subscription for one of your existing locations.`,
        displayButton: true,
        buttonText: 'OK',
      })
        .pipe(take(1))
        .subscribe();
      return;
    }

    this.modalController.create<AddEditLocationComponent, unknown>(AddEditLocationComponent, {account: this.account})
      .pipe(take(1))
      .subscribe();
  }

  editLocation(location: Location): void {
    this.modalController.create<AddEditLocationComponent, unknown>(AddEditLocationComponent, {
      location,
      account: this.account,
    })
      .pipe(take(1))
      .subscribe();
  }

  deleteLocation(id: string): void {
    this.confirmationModalService.confirm({
      content: 'Are you sure you want to delete this location?',
      closeBtnText: 'Cancel',
    })
      .pipe(
        take(1),
        filter((result: ConfirmationModalRef<boolean>) => result.isConfirmed),
        switchMap(() => this.partyService.getUpcoming(id)),
        switchMap((count) => {
          if (count === 0) {
            return of({
              isConfirmed: true,
              close: undefined,
              dismiss: undefined,
            });
          }

          return this.confirmationModalService.confirm({
            content: 'This location has upcoming parties. Are you sure you want to delete it?',
            closeBtnText: 'Cancel',
          });
        }),
        take(1),
        filter((result: ConfirmationModalRef<boolean>) => result.isConfirmed),
        switchMap(() => {
          this.angulartics2.eventTrack.next({
            action: 'deleted location',
            properties: {
              category: 'location',
              gtmCustom: { accountName: this.account.name },
            },
          });

          return this.locationApiService.deleteById(id);
        }),
        catchError((error) => {
          this.alertsService.addError(error?.error?.message);

          return of(null);
        }),
      )
      .subscribe();
  }

  copySettings(locationId: string): void {
    const location = this.locationService.getById(locationId);

    this.modalController.create<CopySettingsModalComponent, unknown>(CopySettingsModalComponent, {
      location,
      locations: this.locations.filter(({enabled}) => enabled),
    })
      .pipe(take(1))
      .subscribe();
  }

  getWaitlistedPartiesByLocation(locationId: string): number | undefined {
    if (!this.waitlistedParties) {
      return undefined;
    }

    return this.waitlistedParties.find(party => party.id === locationId)?.count || 0;
  }

  showAddButton({account}: AuthUser): boolean {
    return account && (account.subscriptionType === this.subscriptionTypes.recurly
      || account.licenses > this.locations.length);
  }

  private setPricePlans(
    locations: Location[],
    pricePlans: Record<string, PricePlan>,
  ): void {
    const dropdownPricePlans: Record<string, PricePlan> = {};

    Object.entries(pricePlans).forEach(([, pricePlan]) => {
      const filteredLocationsByPricePlan = locations
        .filter(loc => loc?.recurly?.planCode === pricePlan.plan_code);

      if (filteredLocationsByPricePlan.length > 0) {
        dropdownPricePlans[pricePlan.plan_code] = pricePlan;
      }
    });

    this.dropdownPricePlans = Object.entries(dropdownPricePlans)
      .map(([, { plan_code, name }]) => ({value: plan_code, title: name}));
  }
}
